import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AudioButton, ChoiceButton, ExerciseCard, Stack } from '@fluent/ui';
import type { ChooseCorrect as ChooseCorrectExercise } from '@fluent/types';
import { useAudio } from '../audio/audioPlayer.js';
import './ChooseCorrect.css';

export interface ChooseCorrectProps {
  exercise: ChooseCorrectExercise;
  /** Display order for the options — defaults to authored order. The lesson
   *  players pass a shuffled pool; flashcards shuffle upstream. */
  options?: readonly string[];
  selected: string | null;
  onSelectedChange: (next: string) => void;
  locked: boolean;
  /** Set after the user submits — drives the post-check correct/wrong reveal. */
  lastVerdict: 'exact' | 'wrong' | 'close' | null;
  /** Card kicker override — defaults to the lesson kicker. Lets other
   *  surfaces (flashcard test mode) reuse the renderer with their own copy. */
  kicker?: string;
}

/**
 * ChooseCorrect — multiple-choice exercise (recognition over production).
 * Show a Georgian prompt + 2–4 English options; user taps the right one,
 * then taps "შემოწმება" in the dock. Distractors are plausible-but-wrong
 * forms (e.g., "I are Giorgi" vs "I am Giorgi") so the user has to think
 * about grammar, not just guess.
 *
 * State sits in the parent (LessonPlayer) so navigation reset is uniform
 * across all exercise types — same pattern as TranslateTap / TranslateTyped.
 */
export function ChooseCorrect({
  exercise,
  options,
  selected,
  onSelectedChange,
  locked,
  lastVerdict,
  kicker,
}: ChooseCorrectProps) {
  const { t } = useTranslation();
  const audio = useAudio(exercise.audio_key);
  const autoPlayedRef = useRef<string | null>(null);
  const shown = options ?? exercise.options;

  // The clip pronounces the answer, so it stays sealed until the user has
  // committed — then it plays itself once, as the reward/reinforcement.
  useEffect(() => {
    if (lastVerdict == null || autoPlayedRef.current === exercise.id) return;
    autoPlayedRef.current = exercise.id;
    audio.play();
  }, [lastVerdict, exercise.id, audio]);

  return (
    <ExerciseCard
      kicker={kicker ?? t('lesson.choose_correct.kicker')}
      prompt={exercise.prompt}
      promptLang="ka"
    >
      {exercise.audio_key && lastVerdict != null && (
        <div className="fluent-choose__audio-row">
          <AudioButton
            size="md"
            playing={audio.playing}
            onClick={() => (audio.playing ? audio.stop() : audio.play())}
            label={t('lesson.choose_correct.audio_aria')}
          />

        </div>
      )}
      <Stack gap={2} className="fluent-choose__options">
        {shown.map((opt, idx) => {
          const isSelected = selected === opt;
          const isAnswer = opt === exercise.answer;
          // After submit, paint the answer green and the wrong pick red.
          const state =
            lastVerdict == null
              ? 'idle'
              : isAnswer
                ? 'correct'
                : isSelected
                  ? 'wrong'
                  : 'idle';
          return (
            <ChoiceButton
              key={opt}
              lang="en"
              bullet={String.fromCharCode(65 + idx)}
              selected={isSelected}
              state={state}
              disabled={locked}
              onClick={() => onSelectedChange(opt)}
            >
              {opt}
            </ChoiceButton>
          );
        })}
      </Stack>
    </ExerciseCard>
  );
}
